import { Policy } from '../page';

interface PolicyListProps {
  policies: Policy[];
  onView: (policy: Policy) => void;
  onEdit: (policy: Policy) => void;
  onDelete: (id: string) => void;
}

export default function PolicyList({ policies, onView, onEdit, onDelete }: PolicyListProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-700';
      case 'Draft': return 'bg-yellow-100 text-yellow-700';
      case 'Archived': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'HR': return 'ri-team-line';
      case 'Safety': return 'ri-shield-check-line';
      case 'Operations': return 'ri-settings-3-line';
      case 'Finance': return 'ri-money-dollar-circle-line';
      case 'IT': return 'ri-computer-line';
      case 'Legal': return 'ri-scales-3-line';
      case 'Quality': return 'ri-award-line';
      case 'Environmental': return 'ri-leaf-line';
      default: return 'ri-file-text-line';
    }
  };

  if (policies.length === 0) {
    return (
      <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-file-list-3-line text-4xl text-gray-400 w-10 h-10 flex items-center justify-center"></i>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Policies Found</h3>
        <p className="text-sm text-gray-600">Try adjusting your search or filters, or add a new policy.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {policies.map((policy) => (
        <div
          key={policy.id}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className={`${getCategoryIcon(policy.category)} text-2xl text-teal-600 w-6 h-6 flex items-center justify-center`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{policy.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(policy.status)}`}>
                    {policy.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{policy.description}</p>
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-folder-line w-4 h-4 flex items-center justify-center"></i>
                    {policy.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    Effective: {new Date(policy.effectiveDate).toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-git-branch-line w-4 h-4 flex items-center justify-center"></i>
                    v{policy.version}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                    Updated by {policy.updatedBy}
                  </span>
                </div>
                {policy.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {policy.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
            <button
              onClick={() => onView(policy)}
              className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-eye-line mr-2 w-4 h-4 inline-flex items-center justify-center"></i>
              View Details
            </button>
            <button
              onClick={() => onEdit(policy)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line w-4 h-4 inline-flex items-center justify-center"></i>
            </button>
            <button
              onClick={() => onDelete(policy.id)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line w-4 h-4 inline-flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
